
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/seo/SEOHead';
import JsonLd from '../../components/seo/JsonLd';

const ServiceAreaPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const serviceAreaSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Mobile Notary Service Area",
    "description": "Mobile notary services throughout Sherman Oaks and San Fernando Valley",
    "provider": {
      "@type": "LocalBusiness",
      "name": "GCM Mobile Notary"
    },
    "areaServed": [
      {
        "@type": "City",
        "name": "Sherman Oaks",
        "containedInPlace": {
          "@type": "State",
          "name": "California"
        }
      },
      {
        "@type": "City",
        "name": "Encino",
        "containedInPlace": {
          "@type": "State",
          "name": "California"
        }
      },
      {
        "@type": "City",
        "name": "Van Nuys",
        "containedInPlace": {
          "@type": "State",
          "name": "California"
        }
      },
      {
        "@type": "City",
        "name": "North Hollywood",
        "containedInPlace": {
          "@type": "State",
          "name": "California"
        }
      }
    ]
  };

  const primaryAreas = [
    {
      name: 'Sherman Oaks',
      description: 'Our home base - serving all neighborhoods with priority scheduling',
      features: ['Same-day service', 'No travel fees', 'Emergency appointments']
    },
    {
      name: 'Encino',
      description: 'Full mobile notary services throughout Encino and surrounding areas',
      features: ['Residential visits', 'Business locations', 'Medical facilities']
    },
    {
      name: 'Van Nuys',
      description: 'Comprehensive notary services for Van Nuys residents and businesses',
      features: ['Court document prep', 'Real estate closings', 'Power of attorney']
    },
    {
      name: 'North Hollywood',
      description: 'Professional notarization services for the NoHo arts district',
      features: ['Entertainment industry docs', 'Loan signings', 'Travel consent forms']
    }
  ];

  const allAreas = [
    'Sherman Oaks', 'Encino', 'Van Nuys', 'North Hollywood', 'Studio City', 'Woodland Hills',
    'Tarzana', 'Reseda', 'Canoga Park', 'Northridge', 'Granada Hills', 'Panorama City',
    'Valley Village', 'Valley Glen', 'Sun Valley', 'Burbank', 'Toluca Lake', 'Universal City',
    'Chatsworth', 'Porter Ranch', 'Winnetka', 'West Hills', 'Calabasas', 'Hidden Hills'
  ];

  const serviceTypes = [
    {
      icon: 'ri-home-4-line',
      title: 'Residential Visits',
      description: 'We come to your home for convenient notarization services'
    },
    {
      icon: 'ri-building-line',
      title: 'Business Locations',
      description: 'Office visits for corporate and business document notarization'
    },
    {
      icon: 'ri-hospital-line',
      title: 'Medical Facilities',
      description: 'Hospital and care facility visits for urgent document needs'
    },
    {
      icon: 'ri-scales-3-line',
      title: 'Legal Offices',
      description: 'Attorney office visits for legal document notarization'
    },
    {
      icon: 'ri-bank-line',
      title: 'Financial Institutions',
      description: 'Bank and credit union visits for loan and financial documents'
    },
    {
      icon: 'ri-car-line',
      title: 'Mobile Locations',
      description: 'Meet at any convenient location throughout our service area'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Mobile Notary Service Area - Sherman Oaks & San Fernando Valley Coverage"
        description="Mobile notary service area covering Sherman Oaks, Encino, Van Nuys, North Hollywood, Studio City, Woodland Hills and entire San Fernando Valley. Fast response times."
        keywords="Mobile Notary Service Area Sherman Oaks, San Fernando Valley Notary Coverage, Encino Mobile Notary, Van Nuys Notary Service, North Hollywood Mobile Notary"
        canonical={`${siteUrl}/service-area`}
        ogTitle="Mobile Notary Service Area - Sherman Oaks & San Fernando Valley Coverage"
        ogDescription="Mobile notary service area covering Sherman Oaks, Encino, Van Nuys, North Hollywood, Studio City, Woodland Hills and entire San Fernando Valley. Fast response times."
        ogUrl={`${siteUrl}/service-area`}
        ogImage={`${siteUrl}/og-image.jpg`}
      />
      <JsonLd data={serviceAreaSchema} />
      
      <Header />
      
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-orange-50 to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Mobile Notary Service Area
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Professional mobile notary services throughout Sherman Oaks and the entire San Fernando Valley. 
            We bring notarization directly to your location.
          </p>
          <a
            href="tel:+18772113011"
            className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-line mr-2"></i>
            Call (877) 211-3011
          </a>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              San Fernando Valley Coverage
            </h2>
            <p className="text-xl text-gray-600">
              Based in Sherman Oaks, serving the entire valley and surrounding areas
            </p>
          </div>
          
          <div className="bg-gray-100 rounded-xl p-8 mb-12">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d105996.39089633896!2d-118.5692!3d34.1808!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c2bf07045279bf%3A0x3a68e0661c9c2b4d!2sSherman%20Oaks%2C%20CA!5e0!3m2!1sen!2sus!4v1635000000000!5m2!1sen!2sus"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="rounded-lg"
            ></iframe>
          </div>
        </div>
      </section>

      {/* Primary Service Areas */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Primary Service Areas
            </h2>
            <p className="text-xl text-gray-600">
              Premium service locations with fastest response times
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {primaryAreas.map((area, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{area.name}</h3>
                <p className="text-gray-600 mb-6">{area.description}</p>
                <div className="space-y-2">
                  {area.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center text-gray-700">
                      <i className="ri-check-line text-teal-600 mr-3"></i>
                      {feature}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* All Service Areas */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Complete Service Coverage
            </h2>
            <p className="text-xl text-gray-600">
              We serve all communities throughout the San Fernando Valley
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {allAreas.map((area, index) => (
              <div key={index} className="bg-orange-50 p-4 rounded-lg text-center hover:bg-orange-100 transition-colors">
                <span className="text-gray-800 font-medium">{area}</span>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <p className="text-gray-600 mb-6">
              Don't see your area listed? We may still be able to serve you. Contact us to confirm availability.
            </p>
            <a
              href="tel:+18772113011"
              className="bg-orange-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Check Service Availability
            </a>
          </div>
        </div>
      </section>

      {/* Service Types */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Where We Provide Service
            </h2>
            <p className="text-xl text-gray-600">
              Professional notarization at any location convenient for you
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {serviceTypes.map((type, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${type.icon} text-2xl text-orange-500`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{type.title}</h3>
                <p className="text-gray-600">{type.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Travel Fees */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-orange-50 p-8 rounded-xl">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Travel Fees & Response Times
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-500 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-map-pin-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Sherman Oaks</h3>
                <p className="text-gray-600 mb-2">$25 Travel fee</p>
                <p className="text-sm text-gray-500">1-2 hour response</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-500 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-road-map-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Primary Areas</h3>
                <p className="text-gray-600 mb-2">$25 travel fee</p>
                <p className="text-sm text-gray-500">2-4 hour response</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-500 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-navigation-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Extended Areas</h3>
                <p className="text-gray-600 mb-2">$50 travel fee</p>
                <p className="text-sm text-gray-500">Same day service</p>
              </div>
            </div>
            
            <div className="text-center mt-8">
              <p className="text-gray-600">
                * Emergency and after-hours services available with additional fees
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Hours */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
            Service Hours
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Regular Hours</h3>
              <div className="space-y-2 text-gray-600">
                <div className="flex justify-between">
                  <span>Monday - Friday:</span>
                  <span>8:00 AM - 8:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span>Saturday:</span>
                  <span>9:00 AM - 6:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span>Sunday:</span>
                  <span>10:00 AM - 4:00 PM</span>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Emergency Service</h3>
              <div className="space-y-2 text-gray-600">
                <div className="flex justify-between">
                  <span>After Hours:</span>
                  <span>Available</span>
                </div>
                <div className="flex justify-between">
                  <span>Weekends:</span>
                  <span>Available</span>
                </div>
                <div className="flex justify-between">
                  <span>Holidays:</span>
                  <span>By appointment</span>
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-4">
                Additional fees apply for emergency and after-hours service
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Schedule Your Mobile Notary Visit?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Professional notary services delivered to your location throughout the San Fernando Valley. 
            Call now to check availability in your area.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+18772113011"
              className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              Call (877) 211-3011
            </a>
            <a
              href="sms:+18772113011"
              className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-message-line mr-2"></i>
              Text Us
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServiceAreaPage;
